import React, { useState, useEffect, useCallback, useRef } from 'react';
import { triggerKpiCalculation as triggerKpiCalculationService, getMonitoringMapClients, getMonitoringMapCtos } from '../../services/apiService';
import { getFirestore, doc, onSnapshot, collection, query, orderBy, where, limit } from 'firebase/firestore';
import { Loader, AlertTriangle, Users, WifiOff, Thermometer, Gauge as GaugeIcon, RefreshCw, ArrowUp, ArrowDown, Network, ChevronDown } from 'lucide-react';
import Gauge from '../common/Gauge';
import KpiHistoryChart from '../common/KpiHistoryChart';
import BadSignalClientsTable from '../common/BadSignalClientsTable';
import NetworkOutageDashboard from '../common/NetworkOutageDashboard';
import MonitoringCtoModal from '../modals/MonitoringCtoModal';
import { format } from 'date-fns';

const KpiCard = ({ title, value, icon: Icon, unit, colorClass = 'text-blue-500', isBlinking = false, change = null }) => {
    const changeIcon = change > 0 ? ArrowUp : ArrowDown;
    const changeColor = change > 0 ? 'text-red-500' : 'text-green-500';

    return (
        <div className={`p-4 bg-slate-100 dark:bg-slate-800/50 rounded-2xl shadow-lg flex items-center transition-all duration-300 ${isBlinking ? 'animate-pulse bg-red-500/10' : ''}`}>
            <div className={`p-3 rounded-full mr-4 ${colorClass.replace('text', 'bg')}/10`}>
                <Icon className={`h-6 w-6 ${colorClass}`} />
            </div>
            <div className="flex-grow">
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">{title}</p>
                <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                        {value}
                        {unit && <span className="text-lg ml-1">{unit}</span>}
                    </p>
                    {change !== null && change !== 0 && (
                        <div className={`flex items-center ${changeColor}`}>
                            {React.createElement(changeIcon, { className: 'h-4 w-4' })}
                            <span className="text-sm font-bold">{Math.abs(change)}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


// Novo componente para o seletor de threshold
const ThresholdSelector = ({ value, onChange }) => {
    const options = Array.from({ length: 11 }, (_, i) => -25 - i); // Gera de -25 a -35

    return (
        <div className="flex items-center gap-2">
            <label htmlFor="threshold-select" className="text-sm font-medium text-slate-600 dark:text-slate-300">
                Threshold:
            </label>
            <select
                id="threshold-select"
                value={value}
                onChange={(e) => onChange(parseInt(e.target.value, 10))}
                className="block w-full max-w-xs pl-3 pr-8 py-1.5 text-sm bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
            >
                {options.map((option) => (
                    <option key={option} value={option}>
                        {option} dBm
                    </option>
                ))}
            </select>
        </div>
    );
};

const NetworkMonitoringPage = ({ currentUser, theme, darkMapEnabled }) => {
    const [kpiData, setKpiData] = useState(null);
    const [indicatorsOpen, setIndicatorsOpen] = useState(false);
    const [kpiHistory, setKpiHistory] = useState([]);
    const [badSignalClients, setBadSignalClients] = useState([]);
    const [filteredMapClients, setFilteredMapClients] = useState(null);
    const [mapSelectedClient, setMapSelectedClient] = useState(null);
    const [manageCtos, setManageCtos] = useState(false);
    const [showAllActiveClients, setShowAllActiveClients] = useState(false);
    const [activeMapClients, setActiveMapClients] = useState([]);
    const [activeMapReady, setActiveMapReady] = useState(false);
    const [activeMapLoading, setActiveMapLoading] = useState(false);
    const [activeMapError, setActiveMapError] = useState('');
    const [mapCtos, setMapCtos] = useState([]);
    const [mapCtosLoading, setMapCtosLoading] = useState(false);
    const [mapCtosError, setMapCtosError] = useState('');
    const ctosLoadedAt = useRef(0);
    const clientsLoadedAt = useRef(0);
    useEffect(() => {
        if (!showAllActiveClients || Date.now() - ctosLoadedAt.current < 60000) return;
        let current = true;
        const controller = new AbortController();
        setMapCtosLoading(true);
        setMapCtosError('');
        getMonitoringMapCtos(controller.signal).then(result => {
            if (!current) return;
            if (!Array.isArray(result?.ctos)) throw new Error('Inventário de CTOs inválido.');
            setMapCtos(result.ctos);
            ctosLoadedAt.current = Date.now();
        }).catch(error => {
            if (current && error.name !== 'AbortError') setMapCtosError('Não foi possível carregar todas as CTOs. Desative e ative o botão para tentar novamente.');
        }).finally(() => { if (current) setMapCtosLoading(false); });
        return () => { current = false; controller.abort(); };
    }, [showAllActiveClients]);
    useEffect(() => {
        if (!showAllActiveClients) return;
        let current = true;
        let directStarted = false;
        let snapshotReady = false;
        const controller = new AbortController();
        setActiveMapLoading(true);
        setActiveMapError('');
        const loadDirect = async () => {
            if (directStarted || !current) return;
            if (!kpiData?.activeClientsReady && Date.now() - clientsLoadedAt.current < 60000) { setActiveMapLoading(false); return; }
            directStarted = true;
            try {
                const result = await getMonitoringMapClients(controller.signal);
                if (!current || snapshotReady) return;
                if (!Array.isArray(result?.clients)) throw new Error('Inventário de clientes inválido.');
                setActiveMapClients(result.clients);
                clientsLoadedAt.current = Date.now();
                if (Array.isArray(result.ctos)) { setMapCtos(result.ctos); ctosLoadedAt.current = Date.now(); }
                setActiveMapReady(true);
                setActiveMapError('');
            } catch (error) {
                if (!current || snapshotReady || error.name === 'AbortError') return;
                setActiveMapError(error.response?.data?.message || 'Não foi possível carregar todos os clientes com login ativo. Mantendo os clientes já carregados no mapa. Desative e ative o botão para tentar novamente.');
            } finally {
                if (current && !snapshotReady) setActiveMapLoading(false);
            }
        };
        if (!kpiData?.activeClientsReady) {
            loadDirect();
            return () => { current = false; controller.abort(); };
        }
        const inventory = collection(getFirestore(), 'kpi_results', 'network_monitoring', 'active_clients');
        const inventoryQuery = kpiData.activeClientsVersion ? query(inventory, where('version', '==', kpiData.activeClientsVersion)) : inventory;
        const unsubscribe = onSnapshot(inventoryQuery, { includeMetadataChanges: true }, snapshot => {
            if (!current) return;
            const chunks = snapshot.docs.map(chunk => chunk.data());
            const clients = chunks.flatMap(chunk => chunk.clients || []);
            const expected = kpiData.activeClientsCount;
            const complete = (!Number.isInteger(expected) || clients.length === expected) &&
                (clients.length > 0 || expected === 0) &&
                (!kpiData.activeClientsVersion || chunks.every(chunk => chunk.version === kpiData.activeClientsVersion));
            if (!complete) {
                // Root metadata and collection listeners may deliver the same atomic write
                // at different times. Keep the last map until a complete inventory arrives.
                if (!snapshot.metadata?.fromCache) loadDirect();
                return;
            }
            snapshotReady = true;
            setActiveMapClients(clients);
            clientsLoadedAt.current = Date.now();
            setActiveMapReady(true);
            setActiveMapError('');
            setActiveMapLoading(false);
        }, () => { snapshotReady = false; loadDirect(); });
        return () => { current = false; controller.abort(); unsubscribe(); };
    }, [showAllActiveClients, kpiData?.activeClientsReady, kpiData?.activeClientsCount, kpiData?.activeClientsVersion]);
    const mapClients = showAllActiveClients ? (activeMapReady ? activeMapClients : badSignalClients) : (filteredMapClients || badSignalClients);
    const selectMapClient = useCallback(client => setMapSelectedClient({ ...client }), []);
    const [status, setStatus] = useState('loading'); // loading, calculating, complete, error
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [triggerLoading, setTriggerLoading] = useState(false);
    const [threshold, setThreshold] = useState(-27); // Novo estado para o threshold
    const [alertState, setAlertState] = useState({ isActive: false });
    const [badSignalHistory, setBadSignalHistory] = useState([]);
    const isInitialMount = useRef(true);

    const triggerKpiCalculation = useCallback(async () => {
        setTriggerLoading(true);
        setError(null);
        try {
            await triggerKpiCalculationService(threshold);
        } catch (err) {
            setError(err.message || 'Falha ao iniciar o processo de cálculo.');
        } finally {
            setTriggerLoading(false);
        }
    }, [threshold]);

    // Efeito para acionar o cálculo de KPI quando o threshold muda (com debounce)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        if (status === 'calculating' || triggerLoading) {
            return;
        }

        const handler = setTimeout(() => {
            triggerKpiCalculation();
        }, 800);

        return () => {
            clearTimeout(handler);
        };
    }, [threshold, triggerKpiCalculation]);

    // Force reset if stuck in calculating for too long (frontend safety net)
    useEffect(() => {
        let timeout;
        if (status === 'calculating') {
            timeout = setTimeout(() => {
                console.warn("Processo de cálculo demorou muito. Resetando estado visual.");
                setStatus('complete'); // Force complete to allow user to retry
                setError("O cálculo demorou mais que o esperado. Tente atualizar novamente.");
            }, 10 * 60000); // Match the backend calculation timeout.
        }
        return () => clearTimeout(timeout);
    }, [status]);

    // Efeito para ouvir o estado e os resultados do cálculo de KPI
    useEffect(() => {
        const db = getFirestore();
        const kpiDocRef = doc(db, 'kpi_results', 'network_monitoring');

        // Listener para o estado do alerta
        const alertStateRef = doc(db, 'alert_state', 'bad_signal_alert');
        const unsubscribeAlert = onSnapshot(alertStateRef, (doc) => {
            if (doc.exists()) {
                setAlertState(doc.data());
            } else {
                setAlertState({ isActive: false });
            }
        });

        // Listener para o histórico de clientes com sinal ruim
        const badSignalHistoryRef = collection(db, 'kpi_results', 'network_monitoring', 'bad_signal_history');
        const qBadSignal = query(badSignalHistoryRef, orderBy('timestamp', 'desc'), limit(2));
        const unsubscribeBadSignal = onSnapshot(qBadSignal, (snapshot) => {
            const history = snapshot.docs.map(doc => doc.data().count);
            setBadSignalHistory(history);
        });

        const unsubscribe = onSnapshot(kpiDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setStatus(data.status);

                if (data.status === 'error') {
                    setError(data.errorMessage || 'Erro no processamento de KPIs.');
                } else if (data.status === 'complete' || data.status === 'calculating') {
                    if (data.kpis) {
                        setKpiData(data);
                        setBadSignalClients(data.badSignalClients || data.kpis?.badSignalClients || []);

                        if (data.lastUpdated && typeof data.lastUpdated.toDate === 'function') {
                            const lastUpdateDate = data.lastUpdated.toDate();
                            setLastUpdated(lastUpdateDate);

                            // Auto-trigger update if data is stale (> 1 hour) and not currently calculating
                            const oneHour = 60 * 60 * 1000;
                            const isStale = (new Date() - lastUpdateDate) > oneHour;

                            // Check if we are not already loading (triggerLoading is from closure, might be stale but usually false on mount)
                            // We use setTimeout to avoid state update during render cycle issues, and to allow UI to settle
                            if (isStale && data.status !== 'calculating') {
                                console.log("Dados obsoletos. Acionando atualização automática.");
                                setTimeout(() => triggerKpiCalculation(), 500);
                            }
                        }
                    }
                }
            } else {
                setStatus('calculating');
                triggerKpiCalculation();
            }
        }, (err) => {
            console.error("Erro no listener de kpi_results:", err);
            setError("Falha ao conectar para receber atualizações de status.");
            setStatus('error');
        });

        return () => {
            unsubscribe();
            unsubscribeAlert();
            unsubscribeBadSignal();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // Efeito para buscar e ouvir o histórico de KPI
    useEffect(() => {
        const db = getFirestore();
        const historyCollectionRef = collection(db, 'kpi_results', 'network_monitoring', 'kpi_history');

        // Calcula o timestamp de 48 horas atrás
        const fortyEightHoursAgo = new Date();
        fortyEightHoursAgo.setHours(fortyEightHoursAgo.getHours() - 48);

        // Query para buscar dados das últimas 48 horas e ouvir novas entradas
        const q = query(
            historyCollectionRef,
            where('timestamp', '>=', fortyEightHoursAgo),
            orderBy('timestamp', 'asc') // Ordena do mais antigo para o mais novo
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const historicalData = querySnapshot.docs
                .filter(doc => doc.data().timestamp && typeof doc.data().timestamp.toDate === 'function')
                .map(doc => {
                    const data = doc.data();
                    const timestamp = data.timestamp.toDate();
                    return {
                        time: format(timestamp, 'HH:mm'),
                        badSignal: data.badSignal,
                        avgRx: data.avgRx,
                        avgTemp: data.avgTemp,
                        fullTimestamp: timestamp.toISOString(), // Para deduplicação e ordenação
                        _timeMs: timestamp.getTime() // ⚡ Bolt: Pre-calculated timestamp for O(1) sort comparisons
                    };
                });

            // Ordena no cliente para garantir a ordem correta, se necessário, embora o Firestore deva fazer isso
            // ⚡ Bolt: Use pre-calculated timestamp (_timeMs) instead of new Date(a.fullTimestamp) for faster sort
            historicalData.sort((a, b) => a._timeMs - b._timeMs);

            setKpiHistory(historicalData);
        }, (err) => {
            console.error("Erro ao buscar histórico de KPIs (kpi_history):", err);
            // Opcional: definir um erro específico para o histórico
        });

        return () => unsubscribe();
    }, []);

    const headerControls = <>
        {manageCtos && <MonitoringCtoModal theme={theme} onClose={() => setManageCtos(false)} />}
        <span className="text-xs text-slate-500 dark:text-slate-400" title={lastUpdated?.toLocaleString('pt-BR')}>Sinal: {lastUpdated ? lastUpdated.toLocaleTimeString('pt-BR') : 'aguardando'}</span>
        <ThresholdSelector value={threshold} onChange={setThreshold} />
        <button onClick={triggerKpiCalculation} disabled={triggerLoading || status === 'calculating'} className="p-2 rounded border border-slate-500/30 disabled:opacity-50" title="Atualizar dados de sinal" aria-label="Atualizar dados de sinal">
            <RefreshCw className={`h-4 w-4 ${triggerLoading || status === 'calculating' ? 'animate-spin' : ''}`} />
        </button>
        {currentUser?.role === 'administrador' && <button type="button" onClick={() => setManageCtos(true)} aria-label="Gerenciar CTOs" title="Gerenciar CTOs / corrigir cidades e OLTs" className="p-2 rounded border border-slate-500/30"><Network size={18} /></button>}
        {status === 'calculating' && <button onClick={() => setStatus('complete')} className="text-xs text-red-500 hover:underline" title="Se travado, clique para resetar">Resetar</button>}
        <button type="button" onClick={() => setShowAllActiveClients(value => !value)} aria-label="Exibir todos os clientes com login ativo" aria-pressed={showAllActiveClients} title={showAllActiveClients ? 'Exibindo todos os clientes com login ativo · clique para exibir apenas sinal ruim' : 'Exibir todos os clientes com login ativo'} className={`p-2 rounded border ${showAllActiveClients ? 'border-green-500 bg-green-500/10 text-green-700 dark:text-green-400' : 'border-slate-500/30'}`}><Users size={18} /></button>
    </>;

    const activeMapNotice = showAllActiveClients && <>
        {mapCtosError ? <p role="alert" className="mb-3 text-sm text-red-600">{mapCtosError}</p> :
            mapCtosLoading ? <p role="status" className="mb-3 text-sm">Carregando todas as CTOs...</p> : null}
        {(
        activeMapError ? <p role="alert" className="mb-3 text-sm text-red-600">{activeMapError}</p> :
        activeMapLoading ? <p role="status" className="mb-3 text-sm">Carregando todos os clientes com login ativo...</p> : null
    )}</>;

    if (status === 'error') {
        return (
            <div className="p-4">
            {activeMapNotice}
            <NetworkOutageDashboard canOpenServiceOrder={['administrador', 'tecnico'].includes(currentUser?.role)} isAdmin={currentUser?.role === 'administrador'} clients={mapClients} ctos={mapCtos} showAllActiveClients={showAllActiveClients} threshold={threshold} theme={theme} monitoringKpis={kpiData?.kpis} headerControls={headerControls} onSelectClient={selectMapClient} darkMapEnabled={theme === 'dark' && darkMapEnabled} />
            <div className="flex flex-col justify-center items-center h-64 text-center text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 p-4 rounded-lg">
                <AlertTriangle className="w-16 h-16 mb-4" />
                <h3 className="text-xl font-bold mb-2">Erro ao Carregar Dados da Rede</h3>
                <p className="max-w-md">{error}</p>
                <button
                    onClick={triggerKpiCalculation}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    disabled={triggerLoading}
                >
                    {triggerLoading ? 'Acionando...' : 'Tentar Novamente'}
                </button>
            </div>
            </div>
        );
    }

    const isLoading = status === 'loading' || (status === 'calculating' && !kpiData);

    return (
        <div className="p-4">
            {activeMapNotice}
            <NetworkOutageDashboard canOpenServiceOrder={['administrador', 'tecnico'].includes(currentUser?.role)} isAdmin={currentUser?.role === 'administrador'} clients={mapClients} ctos={mapCtos} showAllActiveClients={showAllActiveClients} threshold={threshold} theme={theme} monitoringKpis={kpiData?.kpis} headerControls={headerControls} onSelectClient={selectMapClient} darkMapEnabled={theme === 'dark' && darkMapEnabled} />
            {isLoading ? (
                <div className="flex flex-col justify-center items-center h-64">
                    <Loader className="animate-spin h-20 w-20 text-blue-500" />
                    <p className="ml-4 mt-4 text-lg text-slate-600 dark:text-slate-300">Buscando dados da rede IXC...</p>
                    <p className="text-sm text-slate-500">(Isso pode levar alguns minutos na primeira vez)</p>
                </div>
            ) : (
                <>
                    <section className="mb-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/30">
                        <button type="button" aria-expanded={indicatorsOpen} aria-controls="monitoring-indicators" onClick={() => setIndicatorsOpen(open => !open)} className="flex w-full items-center justify-between gap-3 p-4 text-left font-semibold text-slate-800 dark:text-slate-100">
                            Indicadores e gráficos da rede
                            <ChevronDown size={20} aria-hidden="true" className={indicatorsOpen ? 'rotate-180' : ''} />
                        </button>
                        <div id="monitoring-indicators" hidden={!indicatorsOpen}>
                            {indicatorsOpen && <div className="px-4 pb-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                                    <div className="md:col-span-2 lg:col-span-2">
                                        <Gauge value={kpiData?.kpis?.networkHealth || 0} title="Saúde da Rede" />
                                    </div>
                                    <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <KpiCard title="Clientes Fibra Ativos" value={kpiData?.kpis?.totalActiveClients || 0} icon={Users} colorClass="text-green-500" />
                                        <KpiCard
                                            title="Clientes com Sinal Ruim"
                                            value={kpiData?.kpis?.totalBadSignalClients || 0}
                                            icon={WifiOff}
                                            colorClass="text-red-500"
                                            isBlinking={alertState.isActive}
                                            change={badSignalHistory.length === 2 ? badSignalHistory[0] - badSignalHistory[1] : null}
                                        />
                                        <KpiCard title="Temperatura Média ONU" value={kpiData?.kpis?.averageTemperature || 'N/A'} unit="°C" icon={Thermometer} colorClass="text-amber-500" />
                                        <KpiCard title="Potência Rx Média" value={kpiData?.kpis?.averageRxPower || 'N/A'} unit="dBm" icon={GaugeIcon} colorClass="text-indigo-500" />
                                    </div>
                                </div>
                                <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    <KpiHistoryChart
                                        data={kpiHistory}
                                        title="Clientes com Sinal Ruim"
                                        dataKey="badSignal"
                                        strokeColor="#ef4444"
                                    />
                                    <KpiHistoryChart
                                        data={kpiHistory}
                                        title="Potência Rx Média"
                                        dataKey="avgRx"
                                        strokeColor="#8884d8"
                                        unit="dBm"
                                    />
                                    <KpiHistoryChart
                                        data={kpiHistory}
                                        title="Temperatura Média ONU"
                                        dataKey="avgTemp"
                                        strokeColor="#82ca9d"
                                        unit="°C"
                                    />
                                </div>
                            </div>}
                        </div>
                    </section>
                    <div className="mt-8">
                        {kpiData && !kpiData.ctoDiagnostics && (
                            <p role="status" className="mb-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
                                Atualize os dados do Monitoramento para carregar a identificação das CTOs.
                            </p>
                        )}
                        {kpiData?.ctoDiagnostics?.lookupError && (
                            <p role="alert" className="mb-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
                                Não foi possível carregar as CTOs do IXC. As posições das CTOs ficam indisponíveis até a consulta ser concluída com sucesso.
                                <span className="mt-1 block">Detalhes: {kpiData.ctoDiagnostics.lookupError}</span>
                                <span className="mt-1 block">Após corrigir a causa, force a atualização do Monitoramento.</span>
                            </p>
                        )}
                        {(kpiData?.locationDiagnostics?.missing > 0 || kpiData?.locationDiagnostics?.failed > 0) && (
                            <p role="status" className="mb-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
                                {[
                                    [kpiData.locationDiagnostics.missing, 'cliente(s) sem posição'],
                                    [kpiData.locationDiagnostics.failed, 'falha(s) ao consultar ou salvar no IXC'],
                                    [kpiData.locationDiagnostics.noAddress, 'sem endereço'],
                                    [kpiData.locationDiagnostics.noCity, 'com cidade não identificada'],
                                    [kpiData.locationDiagnostics.noMatch, 'endereço(s) sem resultado'],
                                ].filter(([count]) => count > 0).map(([count, label]) => `${count} ${label}`).join(' · ')}.
                            </p>
                        )}
                        <BadSignalClientsTable
                            onFilteredClientsChange={setFilteredMapClients}
                            mapSelectedClient={mapSelectedClient}
                            clients={badSignalClients}
                            currentUser={currentUser}
                            theme={theme}
                            threshold={kpiData?.threshold}
                            lastUpdated={lastUpdated}
                            darkMapEnabled={theme === 'dark' && darkMapEnabled}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default NetworkMonitoringPage;
